import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Loader2,
  CalendarIcon,
  MapPin,
  Clock,
  Shirt,
  Sparkles,
  Snowflake,
  Droplets,
  Truck,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);
  const [locationStatus, setLocationStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    service_type: "",
    preferred_date: null,
    preferred_time: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    notes: "",
    latitude: null,
    longitude: null,
  });

  const services = [
    { id: "wash-fold", name: "Wash & Fold", icon: Shirt, price: "$2.59/lb", description: "Full service laundry" },
    { id: "dry-cleaning", name: "Dry Cleaning", icon: Sparkles, price: "", description: "Professional dry cleaning" },
    { id: "fresh-gear", name: "Fresh Gear", icon: Snowflake, price: "", description: "Ozone sanitization" },
    { id: "water-refills", name: "Water Refills", icon: Droplets, price: "", description: "Purified water" },
  ];

  const timeSlots = [
    "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
  ];

  // Check if user is within service area
  const checkServiceArea = async () => {
    setIsCheckingLocation(true);
    setLocationStatus(null);

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setFormData((prev) => ({ ...prev, latitude, longitude }));

          try {
            const response = await axios.post(`${API}/geo-check`, {
              latitude,
              longitude,
            });
            setLocationStatus(response.data);
          } catch (error) {
            setLocationStatus({
              within_service_area: true,
              message: "Unable to verify location. You can still proceed with booking.",
            });
          } finally {
            setIsCheckingLocation(false);
          }
        },
        () => {
          setIsCheckingLocation(false);
          setLocationStatus({
            within_service_area: true,
            message: "Location access denied. Please ensure you're within 25km of Elmira.",
          });
        }
      );
    } else {
      setIsCheckingLocation(false);
      setLocationStatus({
        within_service_area: true,
        message: "Geolocation not supported. Please ensure you're within 25km of Elmira.",
      });
    }
  };

  useEffect(() => {
    checkServiceArea();
  }, []);

  const validateStep = (currentStep) => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.service_type) newErrors.service_type = "Please select a service";
    } else if (currentStep === 2) {
      if (!formData.preferred_date) newErrors.preferred_date = "Please select a date";
      if (!formData.preferred_time) newErrors.preferred_time = "Please select a time";
    } else if (currentStep === 3) {
      if (!formData.name.trim()) newErrors.name = "Name is required";
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email";
      }
      if (!formData.phone.trim()) {
        newErrors.phone = "Phone is required";
      } else if (!/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone)) {
        newErrors.phone = "Please enter a valid phone number";
      }
      if(!formData.address.trim()) newErrors.address = "Address is required";
      if(!formData.city.trim()) newErrors.city = "City is required";
      if (!formData.postal_code.trim()) {
        newErrors.postal_code = "Postal code is required";
      } else {
        const postalCode = formData.postal_code.trim().toUpperCase();
        if (!/^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ -]?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/.test(postalCode)) {
          newErrors.postal_code = "Please enter a valid postal code";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleServiceSelect = (serviceId) => {
    setFormData((prev) => ({ ...prev, service_type: serviceId }));
    if (errors.service_type) {
      setErrors((prev) => ({ ...prev, service_type: "" }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateSelect = (date) => {
    setFormData((prev) => ({ ...prev, preferred_date: date }));
    if (errors.preferred_date) {
      setErrors((prev) => ({ ...prev, preferred_date: "" }));
    }
  };

  const handleTimeSelect = (time) => {
    setFormData((prev) => ({ ...prev, preferred_time: time }));
    if (errors.preferred_time) {
      setErrors((prev) => ({ ...prev, preferred_time: "" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);

    try {
      const{ city, ...rest } = formData;
      const payload = {
        ...rest,
        address: `${formData.address}, ${formData.city}`,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, "yyyy-MM-dd") : "",
      };

      await axios.post(`${API}/bookings`, payload);
      toast.success("Booking submitted successfully! We'll contact you shortly.");
      setStep(5); // Success step
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to submit booking. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedService = services.find((s) => s.id === formData.service_type);

  // Progress indicator
  const ProgressSteps = () => (
    <div className="flex justify-center mb-8">
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                s < step
                  ? "bg-sky-600 text-white"
                  : s === step
                  ? "bg-sky-600 text-white ring-4 ring-sky-100"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              {s < step ? <CheckCircle2 className="w-5 h-5" /> : s}
            </div>
            {s < 4 && (
              <div
                className={`w-12 h-1 mx-1 rounded ${
                  s < step ? "bg-sky-600" : "bg-slate-200"
                }`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Step labels
  const stepLabels = ["Service", "Date & Time", "Your Info", "Review"];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-sky-50 to-sky-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Book a Pickup</h1>
          <p className="text-slate-600 mt-2">Schedule your laundry service in just a few steps</p>
        </div>

        {/* Location Warning */}
        {locationStatus && !locationStatus.within_service_area && (
          <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Outside Service Area</p>
              <p className="text-sm text-amber-700">{locationStatus.message}</p>
            </div>
          </div>
        )}

        {step < 5 && (
          <>
            {/* Progress */}
            <ProgressSteps />
            <div className="flex justify-center gap-8 mb-8 text-sm">
              {stepLabels.map((label, idx) => (
                <span
                  key={label}
                  className={idx + 1 === step ? "text-sky-600 font-medium" : "text-slate-400"}
                >
                  {label}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <Card className="border-slate-100 shadow-lg" data-testid="step-1-service">
            <CardHeader>
              <CardTitle>Select a Service</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceSelect(service.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                      formData.service_type === service.id
                        ? "border-sky-600 bg-sky-50"
                        : "border-slate-200 hover:border-sky-300"
                    }`}
                    data-testid={`service-option-${service.id}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        formData.service_type === service.id
                          ? "bg-sky-600 text-white"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <service.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{service.name}</p>
                      <p className="text-sm text-slate-500">{service.description}</p>
                    </div>
                    <span className="text-sky-600 font-medium">{service.price}</span>
                  </button>
                ))}
              </div>
              {errors.service_type && (
                <p className="text-red-500 text-sm">{errors.service_type}</p>
              )}
              <div className="pt-4">
                <Button
                  onClick={nextStep}
                  className="w-full bg-sky-600 hover:bg-sky-700 rounded-full"
                  data-testid="next-step-btn"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Date & Time */}
        {step === 2 && (
          <Card className="border-slate-100 shadow-lg" data-testid="step-2-datetime">
            <CardHeader>
              <CardTitle>Choose Date & Time</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Picker */}
              <div className="space-y-2">
                <Label>Preferred Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        !formData.preferred_date && "text-muted-foreground"
                      } ${errors.preferred_date ? "border-red-500" : ""}`}
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.preferred_date
                        ? format(formData.preferred_date, "PPP")
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.preferred_date}
                      onSelect={handleDateSelect}
                      disabled={[
                        { before: new Date() }, 
                        { dayOfWeek: [1, 2, 4] }
                      ]} // Disable past dates and Mon/Tue/Thu
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {errors.preferred_date && (
                  <p className="text-red-500 text-sm">{errors.preferred_date}</p>
                )}
              </div>

              {/* Time Selection */}
              <div className="space-y-2">
                <Label>Preferred Time *</Label>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map((time) => (
                    <button
                      key={time}
                      onClick={() => handleTimeSelect(time)}
                      className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                        formData.preferred_time === time
                          ? "border-sky-600 bg-sky-50 text-sky-700"
                          : "border-slate-200 hover:border-sky-300"
                      }`}
                      data-testid={`time-slot-${time.replace(/\s+/g, '-')}`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
                {errors.preferred_time && (
                  <p className="text-red-500 text-sm">{errors.preferred_time}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="flex-1 rounded-full"
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={nextStep}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 rounded-full"
                  data-testid="next-step-btn"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Contact Info */}
        {step === 3 && (
          <Card className="border-slate-100 shadow-lg" data-testid="step-3-contact">
            <CardHeader>
              <CardTitle>Your Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className={errors.name ? "border-red-500" : ""}
                    data-testid="booking-name-input"
                  />
                  {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className={errors.email ? "border-red-500" : ""}
                    data-testid="booking-email-input"
                  />
                  {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className={errors.phone ? "border-red-500" : ""}
                  data-testid="booking-phone-input"
                />
                {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address (for pickup)*</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main St, Apt 4B"
                  className={errors.address ? "border-red-500" : ""}
                  data-testid="booking-address-input"
                />
                {errors.address && <p className="text-red-500 text-sm">{errors.address}</p>}
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Your city"
                    className={errors.city ? "border-red-500" : ""}
                    data-testid="booking-city-input"
                  />
                  {errors.city && <p className="text-red-500 text-sm">{errors.city}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal Code *</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    placeholder="Your postal code"
                    className={errors.postal_code ? "border-red-500" : ""}
                    data-testid="booking-postalcode-input"
                  />
                  {errors.postal_code && <p className="text-red-500 text-sm">{errors.postal_code}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Special Instructions</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any special requests or notes..."
                  rows={3}
                  data-testid="booking-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="flex-1 rounded-full"
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={nextStep}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 rounded-full"
                  data-testid="next-step-btn"
                >
                  Review Booking
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <Card className="border-slate-100 shadow-lg" data-testid="step-4-review">
            <CardHeader>
              <CardTitle>Review Your Booking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                  {selectedService && (
                    <>
                      <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center text-sky-600">
                        <selectedService.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{selectedService.name}</p>
                        <p className="text-sky-600">{selectedService.price}</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="grid gap-3">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-700">
                      {formData.preferred_date
                        ? format(formData.preferred_date, "EEEE, MMMM d, yyyy")
                        : "Not selected"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-700">{formData.preferred_time || "Not selected"}</span>
                  </div>
                  {formData.address && formData.city && formData.postal_code && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-700">{formData.address},</span>
                      <span className="text-slate-700">{formData.city},</span>
                      <span className="text-slate-700">{formData.postal_code}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
                <h3 className="font-semibold text-slate-900">Contact Information</h3>
                <div className="grid gap-2 text-sm">
                  <p><span className="text-slate-500">Name:</span> {formData.name}</p>
                  <p><span className="text-slate-500">Email:</span> {formData.email}</p>
                  <p><span className="text-slate-500">Phone:</span> {formData.phone}</p>
                  {formData.notes && (
                    <p><span className="text-slate-500">Notes:</span> {formData.notes}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="flex-1 rounded-full"
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 rounded-full"
                  data-testid="submit-booking-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Confirm Booking"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <Card className="border-slate-100 shadow-lg" data-testid="step-5-success">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-sky-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Booking Confirmed!</h2>
              <p className="text-slate-600 mb-6">
                Thank you for your booking. We've sent a confirmation email and SMS to your contact details.
                We'll be in touch shortly to confirm your pickup.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button variant="outline" className="rounded-full px-8" data-testid="back-home-btn">
                    Back to Home
                  </Button>
                </Link>
                <Link to="/services">
                  <Button className="bg-sky-600 hover:bg-sky-700 rounded-full px-8" data-testid="view-services-btn">
                    View Services
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
